import React, { useEffect, useState, useRef } from 'react';
import api from '../api';

interface User { id: number; email: string; full_name: string; org_layer: string; department_id: number; department_name: string; manager_id: number; manager_name: string; start_date?: string | null; is_active: boolean; first_login_done: boolean; }
interface Dept { id: number; name: string; }

const layerLabel: Record<string, string> = { employee: 'עובד', manager: 'מנהל', hq: 'מטה', ceo: 'מנהלת הרשות' };

export default function AdminUsers() {
  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Dept[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [form, setForm] = useState({ full_name: '', email: '', org_layer: 'employee', department_id: '', manager_id: '', start_date: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ created: number; errors: string[] } | null>(null);
  const [newDeptName, setNewDeptName] = useState('');

  const load = async () => {
    const [usersRes, deptsRes] = await Promise.all([api.get('/users/'), api.get('/users/departments')]);
    setUsers(usersRes.data);
    setDepartments(deptsRes.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditUser(null);
    setForm({ full_name: '', email: '', org_layer: 'employee', department_id: '', manager_id: '', start_date: '' });
    setError('');
    setShowModal(true);
  };

  const openEdit = (u: User) => {
    setEditUser(u);
    setForm({ full_name: u.full_name, email: u.email, org_layer: u.org_layer, department_id: u.department_id?.toString() || '', manager_id: u.manager_id?.toString() || '', start_date: u.start_date || '' });
    setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        department_id: form.department_id ? parseInt(form.department_id) : null,
        start_date: form.start_date || null,
        manager_id: form.manager_id ? parseInt(form.manager_id) : null,
      };
      if (editUser) {
        await api.put(`/users/${editUser.id}`, payload);
        setSuccess('עובד עודכן בהצלחה');
      } else {
        await api.post('/users/', payload);
        setSuccess('עובד נוסף ונשלחה הזמנה במייל');
      }
      setShowModal(false);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'שגיאה');
    } finally {
      setSaving(false);
    }
  };

  const handleResendSetup = async (userId: number) => {
    try {
      await api.post(`/users/${userId}/resend-setup`);
      setSuccess('קישור הגדרה נשלח מחדש');
    } catch { setError('שגיאה בשליחת קישור'); }
  };

  const handleDeactivate = async (u: User) => {
    if (!window.confirm(`${u.is_active ? 'השבת' : 'הפעל'} את ${u.full_name}?`)) return;
    await api.put(`/users/${u.id}`, { is_active: !u.is_active });
    await load();
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/users/import-excel', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(data);
      await load();
    } catch (err: any) {
      setError('שגיאה בייבוא הקובץ');
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleAddDept = async () => {
    if (!newDeptName.trim()) return;
    await api.post('/users/departments', { name: newDeptName.trim() });
    setNewDeptName('');
    const { data } = await api.get('/users/departments');
    setDepartments(data);
  };

  const managers = users.filter(u => u.org_layer === 'manager' || u.org_layer === 'hq' || u.org_layer === 'ceo');
  const filtered = users.filter(u => !filter || u.full_name.includes(filter) || u.email.includes(filter) || u.department_name?.includes(filter));

  if (loading) return <div className="loading"><div className="spinner" />טוען...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <h1>ניהול עובדים</h1>
        <div className="page-header-actions">
          <button className="btn btn-primary" onClick={openCreate}>+ הוסף עובד</button>
          <button className="btn btn-secondary" onClick={() => fileRef.current?.click()} disabled={importing}>
            {importing ? 'מייבא...' : '⬆ ייבא מ-Excel'}
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleImport} />
        </div>
      </div>

      {success && <div className="alert alert-success">{success}<button style={{ float: 'left', background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setSuccess('')}>✕</button></div>}
      {error && <div className="alert alert-error">{error}</div>}

      {importResult && (
        <div className="alert alert-info">
          יובאו {importResult.created} עובדים.
          {importResult.errors.length > 0 && <div>{importResult.errors.join(' | ')}</div>}
        </div>
      )}

      {/* Excel format help */}
      <div className="card" style={{ marginBottom: '20px', background: 'var(--blue-50)' }}>
        <div className="text-sm text-muted">
          <strong>פורמט קובץ Excel לייבוא:</strong> עמודות לפי הסדר: שם מלא | מנהל/ת ישיר/ה (שם או מייל) | מחלקה | כתובת מייל | תאריך התחלה | שכבה (עובד/מנהל/מטה/מנהלת הרשות). שורה ראשונה = כותרות. יש להוסיף מנהלים לפני העובדים שלהם. הוספה וייבוא אינם שולחים מייל – המייל עם הקישור להגדרת סיסמה נשלח לכל משתתף/ת מסומן/ת כשמפעילים תהליך (או ידנית בכפתור "שלח קישור").
        </div>
      </div>

      {/* Dept quick-add */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <input className="form-input" style={{ maxWidth: '260px' }} placeholder="שם מחלקה חדשה" value={newDeptName} onChange={e => setNewDeptName(e.target.value)} />
          <button className="btn btn-secondary btn-sm" onClick={handleAddDept}>הוסף מחלקה</button>
          <span className="text-muted text-sm">מחלקות: {departments.map(d => d.name).join(', ')}</span>
        </div>
      </div>

      <input className="form-input" placeholder="חיפוש..." style={{ maxWidth: '300px', marginBottom: '16px' }} value={filter} onChange={e => setFilter(e.target.value)} />

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>שם</th>
              <th>מייל</th>
              <th>שכבה</th>
              <th>מחלקה</th>
              <th>מנהל</th>
              <th>תאריך התחלה</th>
              <th>סטטוס</th>
              <th>פעולות</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.5 }}>
                <td style={{ fontWeight: 600 }}>{u.full_name}</td>
                <td style={{ direction: 'ltr', textAlign: 'right', fontSize: '13px' }}>{u.email}</td>
                <td>{layerLabel[u.org_layer]}</td>
                <td>{u.department_name || '—'}</td>
                <td>{u.manager_name || '—'}</td>
                <td>
                  {u.start_date ? new Date(u.start_date).toLocaleDateString('he-IL') : '—'}
                  {u.start_date && (Date.now() - new Date(u.start_date).getTime()) < 90 * 86400000 && (
                    <span className="badge badge-draft" style={{ marginRight: '6px' }}>פחות מ-3 חודשים</span>
                  )}
                </td>
                <td>
                  {!u.is_active ? <span className="badge badge-pending">לא פעיל</span>
                    : u.first_login_done ? <span className="badge badge-submitted">פעיל</span>
                    : <span className="badge badge-draft">טרם הגדיר סיסמה</span>}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => openEdit(u)}>ערוך</button>
                    {u.is_active && !u.first_login_done && (
                      <button className="btn btn-secondary btn-sm" onClick={() => handleResendSetup(u.id)}>שלח קישור</button>
                    )}
                    <button className="btn btn-secondary btn-sm" onClick={() => handleDeactivate(u)}>
                      {u.is_active ? 'השבת' : 'הפעל'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-title">{editUser ? 'עריכת עובד' : 'הוספת עובד חדש'}</div>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-group">
              <label className="form-label">שם מלא</label>
              <input className="form-input" value={form.full_name} onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">כתובת מייל</label>
              <input className="form-input" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} disabled={!!editUser} style={{ direction: 'ltr', textAlign: 'right' }} />
            </div>
            <div className="form-group">
              <label className="form-label">תאריך התחלה בארגון</label>
              <input className="form-input" type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">שכבה ארגונית</label>
              <select className="form-input" value={form.org_layer} onChange={e => setForm(p => ({ ...p, org_layer: e.target.value }))}>
                <option value="employee">עובד</option>
                <option value="manager">מנהל</option>
                <option value="hq">מטה</option>
                <option value="ceo">מנהלת הרשות</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">מחלקה</label>
              <select className="form-input" value={form.department_id} onChange={e => setForm(p => ({ ...p, department_id: e.target.value }))}>
                <option value="">ללא מחלקה</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">מנהל ישיר</label>
              <select className="form-input" value={form.manager_id} onChange={e => setForm(p => ({ ...p, manager_id: e.target.value }))}>
                <option value="">ללא מנהל</option>
                {managers.map(m => <option key={m.id} value={m.id}>{m.full_name}</option>)}
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>ביטול</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'שומר...' : editUser ? 'שמור שינויים' : 'הוסף עובד'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
